import { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

const ITEMS_PER_PAGE = 8;

export default function FormacaoPage() {
  const { dark } = useTheme();

  const [formacoes, setFormacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Modais
  const [viewingFormacao, setViewingFormacao] = useState(null);
  const [assigningFormacao, setAssigningFormacao] = useState(null);
  const [viewingPerguntasFormacao, setViewingPerguntasFormacao] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [respostasMap, setRespostasMap] = useState({});
  const [perguntasLoading, setPerguntasLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Estados do modal de atribuição
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignType, setAssignType] = useState("departamento");
  const [selectedTarget, setSelectedTarget] = useState("");
  const [assignError, setAssignError] = useState("");

  // ══════════ Tokens de cor ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        bgInput: "#1a2232",
        bgDropdown: "#111827",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)",
        overlay: "rgba(8,12,20,0.8)",
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        bgInput: "#fafbfd",
        bgDropdown: "#ffffff",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
        success: "#16a34a",
        successBg: "#f0fdf4",
        warning: "#d97706",
        warningBg: "#fffbeb",
        overlay: "rgba(15,23,42,0.4)",
      };

  // ── Carregar formações ───────────────────────────────────
  const loadFormacoes = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await Api.getFormacao();
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setFormacoes(list);
    } catch {
      setErrorMsg("Não foi possível carregar as formações.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormacoes();
  }, [loadFormacoes]);

  useEffect(() => {
    if (!notification) return;
    const id = setTimeout(() => setNotification(null), 3500);
    return () => clearTimeout(id);
  }, [notification]);

  useEffect(() => setPage(1), [search]);

  // ── Filtro e paginação ──────────────────────────────────
  const filtered = useMemo(() => {
    if (!search) return formacoes;
    const q = search.toLowerCase();
    return formacoes.filter((f) => {
      const titulo = (f.titulo || "").toLowerCase();
      const plain = (f.texto || "").replace(/<[^>]*>/g, "").toLowerCase();
      return titulo.includes(q) || plain.includes(q);
    });
  }, [formacoes, search]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  // ── Abrir modais ────────────────────────────────────────
  const openView = (formacao) => setViewingFormacao(formacao);
  const closeView = () => setViewingFormacao(null);

  const openAssign = async (formacao) => {
    setAssigningFormacao(formacao);
    setAssignError("");
    setAssignType("departamento");
    setSelectedTarget("");

    setAssignLoading(true);
    try {
      const resUsers = await Api.getUserFormacao({ id: formacao.id });
      const usersArr = Array.isArray(resUsers?.data) ? resUsers.data : Array.isArray(resUsers) ? resUsers : [];
      setAssignedUsers(usersArr);

      if (departamentos.length === 0) {
        const resDep = await Api.getDepartamento();
        const depArr = Array.isArray(resDep?.data) ? resDep.data : Array.isArray(resDep) ? resDep : [];
        setDepartamentos(depArr);
      }

      if (usuarios.length === 0) {
        const resUserList = await Api.getUser();
        const userArr = Array.isArray(resUserList?.data) ? resUserList.data : Array.isArray(resUserList) ? resUserList : [];
        setUsuarios(userArr);
      }
    } catch {
      setAssignError("Erro ao carregar dados para atribuição.");
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssign = () => {
    setAssigningFormacao(null);
    setAssignedUsers([]);
    setAssignError("");
  };

  // ── Modal de questionário ──────────────────────────────
  const openPerguntas = async (formacao) => {
    
    setViewingPerguntasFormacao(formacao);
    setPerguntasLoading(true);
    try {
      const resP = await Api.getPergunta({ id_formacao: formacao.id });
      const perguntasArr = Array.isArray(resP?.data) ? resP.data : Array.isArray(resP) ? resP : [];
      setPerguntas(perguntasArr);
      const map = {};
      for (const p of perguntasArr) {
        const resR = await Api.getResposta({ id_pergunta: p.id });
        const respostasArr = Array.isArray(resR?.data) ? resR.data : Array.isArray(resR) ? resR : [];
        map[p.id] = respostasArr;
      }
      setRespostasMap(map);
    } catch {
      setNotification({ type: "error", text: "Erro ao carregar questionário." });
    } finally {
      setPerguntasLoading(false);
    }
  };

  const closePerguntas = () => {
    setViewingPerguntasFormacao(null);
    setPerguntas([]);
    setRespostasMap({});
  };

  // ── Atribuir ────────────────────────────────────────────
  const handleAssign = async () => {
    if (!assigningFormacao || !selectedTarget) {
      setAssignError("Selecione um destino.");
      return;
    }
    setAssignLoading(true);
    setAssignError("");
    try {
      if (assignType === "departamento") {
        await Api.addDepartamentoFormacao({ id: assigningFormacao.id, id_departamento: selectedTarget });
      } else {
        await Api.addUserFormacao({ id: assigningFormacao.id, id_user: selectedTarget });
      }
      const res = await Api.getUserFormacao({ id: assigningFormacao.id });
      const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setAssignedUsers(arr);
      setSelectedTarget("");
      setNotification({ type: "success", text: "Formação atribuída com sucesso." });
    } catch {
      setAssignError("Erro ao atribuir a formação.");
    } finally {
      setAssignLoading(false);
    }
  };

  // ── Remover atribuição ──────────────────────────────────
  const handleRemove = async (userId) => {
    if (!assigningFormacao) return;
    try {
      await Api.deleteUserFormacao({ id: assigningFormacao.id, id_user: userId });
      const res = await Api.getUserFormacao({ id: assigningFormacao.id });
      const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setAssignedUsers(arr);
      setNotification({ type: "success", text: "Atribuição removida." });
    } catch {
      setNotification({ type: "error", text: "Erro ao remover." });
    }
  };

  // ══════════════════ Estilos CSS ══════════════════════
  const styles = `
    .frm-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .frm-container { max-width: 1200px; margin: 0 auto; }
    .frm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; }
    .frm-heading { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; margin-bottom: 4px; }
    .frm-sub { font-size: 0.88rem; color: ${t.textMuted}; font-weight: 400; }

    .frm-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
    .frm-search-box { display: flex; align-items: center; gap: 8px; background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 14px; padding: 0 16px; height: 44px; width: 300px; transition: border 0.2s, box-shadow 0.2s; }
    .frm-search-box:focus-within { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.18); }
    .frm-search-box input { background: none; border: none; outline: none; font-size: 0.86rem; color: ${t.textPrimary}; width: 100%; }
    .frm-search-box input::placeholder { color: ${t.textMuted}; }

    .frm-table-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; overflow: hidden; }
    .frm-table { width: 100%; border-collapse: collapse; }
    .frm-table thead th { text-align: left; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${t.textMuted}; padding: 14px 22px; background: ${t.bgHover}; border-bottom: 1px solid ${t.border}; }
    .frm-table tbody td { padding: 14px 22px; font-size: 0.88rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; vertical-align: middle; }
    .frm-table tbody tr:last-child td { border-bottom: none; }
    .frm-table tbody tr:hover { background: ${t.rowHover}; }

    .frm-title-cell { font-weight: 600; color: ${t.textPrimary}; }

    .frm-badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.03em; }
    .frm-badge.alta { background: ${t.dangerBg}; color: ${t.danger}; }
    .frm-badge.media { background: ${t.warningBg}; color: ${t.warning}; }
    .frm-badge.baixa { background: ${t.successBg}; color: ${t.success}; }

    .frm-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
    .frm-icon-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid ${t.border}; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: 0.2s; }
    .frm-icon-btn:hover { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .frm-icon-btn.danger:hover { background: ${t.dangerBg}; color: ${t.danger}; border-color: ${t.danger}; }

    .frm-pagination { display: flex; align-items: center; justify-content: space-between; padding: 16px 22px; border-top: 1px solid ${t.border}; color: ${t.textSecondary}; font-size: 0.84rem; }
    .frm-pagination-controls { display: flex; align-items: center; gap: 4px; }
    .frm-pagination-btn { width: 34px; height: 34px; border-radius: 8px; border: 1px solid ${t.border}; background: ${t.bgCard}; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: 0.2s; }
    .frm-pagination-btn:hover:not(:disabled) { background: ${t.bgHover}; color: ${t.textActive}; }
    .frm-pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .frm-pagination-num { width: 34px; height: 34px; border-radius: 8px; border: none; background: transparent; color: ${t.textSecondary}; cursor: pointer; font-weight: 500; }
    .frm-pagination-num.active { background: ${t.bgActive}; color: ${t.textActive}; font-weight: 700; }

    .frm-overlay { position: fixed; inset: 0; background: ${t.overlay}; backdrop-filter: blur(6px); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 20px; }
    .frm-modal { background: ${t.bgDropdown}; border: 1px solid ${t.border}; border-radius: 22px; box-shadow: ${t.shadow}; width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; }
    .frm-modal.wide { max-width: 900px; }
    .frm-modal-header { padding: 24px 28px 20px; border-bottom: 1px solid ${t.border}; display: flex; align-items: center; justify-content: space-between; }
    .frm-modal-title { font-size: 1.15rem; font-weight: 700; color: ${t.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 80%; }
    .frm-modal-close { width: 32px; height: 32px; border-radius: 10px; border: none; background: ${t.bgHover}; color: ${t.textSecondary}; display: flex; align-items: center; justify-content: center; cursor: pointer; }
    .frm-modal-close:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .frm-modal-body { padding: 22px 28px; }
    .frm-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }
    .frm-field label { font-size: 0.78rem; font-weight: 600; color: ${t.textSecondary}; }
    .frm-select, .frm-input { height: 44px; border-radius: 12px; border: 1px solid ${t.border}; background: ${t.bgInput}; color: ${t.textPrimary}; padding: 0 16px; font-size: 0.86rem; outline: none; width: 100%; }
    .frm-select:focus, .frm-input:focus { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15); }
    .frm-btn-primary, .frm-btn-secondary { height: 42px; padding: 0 22px; border-radius: 12px; font-weight: 600; font-size: 0.84rem; cursor: pointer; transition: 0.2s; display: inline-flex; align-items: center; gap: 8px; border: none; }
    .frm-btn-primary { background: linear-gradient(135deg, #00aaff, #0044ee); color: #fff; }
    .frm-btn-primary:hover:not(:disabled) { box-shadow: 0 6px 18px rgba(0,100,255,0.45); transform: translateY(-1px); }
    .frm-btn-secondary { background: transparent; border: 1px solid ${t.border}; color: ${t.textSecondary}; }
    .frm-btn-secondary:hover { background: ${t.bgHover}; color: ${t.textPrimary}; }
    .frm-btn-primary:disabled, .frm-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
    .frm-assigned-list { margin-top: 20px; }
    .frm-assigned-item { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 12px; background: ${t.bgHover}; border: 1px solid ${t.border}; margin-bottom: 8px; }
    .frm-assigned-item span { font-size: 0.84rem; color: ${t.textPrimary}; }
    .frm-assigned-item .frm-remove-btn { background: none; border: none; color: ${t.textMuted}; cursor: pointer; transition: 0.2s; }
    .frm-assigned-item .frm-remove-btn:hover { color: ${t.danger}; }
    .frm-error-banner { background: ${t.dangerBg}; color: ${t.danger}; padding: 14px 20px; border-radius: 14px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

    .frm-view-content {
      background: ${t.bgInput};
      border: 1px solid ${t.border};
      border-radius: 14px;
      padding: 32px;
      max-height: 70vh;
      overflow-y: auto;
      line-height: 1.8;
      color: ${t.textPrimary};
    }
    .frm-view-content h1, .frm-view-content h2, .frm-view-content h3 { margin-top: 0; }
    .frm-view-content p { margin: 0.8em 0; }

    .frm-pergunta-item { margin-bottom: 20px; }
    .frm-pergunta-titulo { font-weight: 600; color: ${t.textPrimary}; margin-bottom: 8px; }
    .frm-resposta-lista { list-style: none; padding-left: 20px; }
    .frm-resposta-lista li { padding: 4px 0; font-size: 0.85rem; }
    .frm-resposta-correta { color: ${t.success}; font-weight: 700; }
    .frm-resposta-errada { color: ${t.textSecondary}; }

    .frm-toast { position: fixed; bottom: 28px; right: 28px; z-index: 900; display: flex; align-items: center; gap: 12px; padding: 16px 22px; border-radius: 16px; background: ${t.bgDropdown}; border: 1px solid ${t.border}; box-shadow: ${t.shadow}; font-size: 0.86rem; font-weight: 500; color: ${t.textPrimary}; }
    .frm-toast-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

    .mini-spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; animation: spin 0.7s linear infinite; display: inline-block; }
    @keyframes spin { to { transform: rotate(360deg); } }

    @media (max-width: 640px) {
      .frm-table thead { display: none; }
      .frm-table tbody tr { display: flex; flex-direction: column; padding: 16px; }
      .frm-table tbody td { padding: 0; border-bottom: none; }
      .frm-actions { justify-content: flex-start; margin-top: 6px; }
      .frm-search-box { width: 100%; }
    }
  `;

  return (
    <div className="frm-page">
      <style>{styles}</style>
      <div className="frm-container">
        <div className="frm-header">
          <div>
            <h1 className="frm-heading">Formações</h1>
            <p className="frm-sub">Visualizar e atribuir formações geradas</p>
          </div>
        </div>

        {errorMsg && <div className="frm-error-banner">⚠️ {errorMsg}</div>}

        <div className="frm-toolbar">
          <div className="frm-search-box">
            <span style={{color: t.textMuted}}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
            <input placeholder="Pesquisar por título ou conteúdo..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="frm-table-card">
          {loading ? (
            <div>{Array.from({length:5}).map((_,i)=><div key={i} style={{padding:"14px 22px", display:"flex", gap:12}}><div style={{width:120, height:12, borderRadius:6, background:t.bgHover}}/><div style={{width:80, height:12, borderRadius:6, background:t.bgHover}}/><div style={{width:100, height:12, borderRadius:6, background:t.bgHover}}/><div style={{flex:1, height:12, borderRadius:6, background:t.bgHover}}/></div>)}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding:"60px", textAlign:"center", color:t.textMuted}}>Nenhuma formação encontrada</div>
          ) : (
            <>
              <table className="frm-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Dificuldade</th>
                    <th>Data de Criação</th>
                    <th style={{textAlign:"right"}}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(f => (
                    <tr key={f.id}>
                      <td>
                        <span className="frm-title-cell" title={f.titulo || "Sem título"}>
                          {f.titulo || "Sem título"}
                        </span>
                      </td>
                      <td>
                        <span className={`frm-badge ${f.dificuldade?.toUpperCase() === "ALTA" ? "alta" : f.dificuldade?.toUpperCase() === "MÉDIA" || f.dificuldade?.toUpperCase() === "MÉDIA" ? "media" : "baixa"}`}>
                          {f.dificuldade}
                        </span>
                      </td>
                      <td style={{color: t.textSecondary}}>
                        {f.created_at ? new Date(f.created_at).toLocaleDateString("pt-PT") : "—"}
                      </td>
                      <td>
                        <div className="frm-actions">
                          <button className="frm-icon-btn" title="Visualizar" onClick={() => openView(f)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <button className="frm-icon-btn" title="Questionário" onClick={() => openPerguntas(f)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          </button>
                          <button className="frm-icon-btn" title="Atribuir" onClick={() => openAssign(f)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="frm-pagination">
                <span>{paginated.length} de {filtered.length}</span>
                <div className="frm-pagination-controls">
                  <button className="frm-pagination-btn" disabled={safePage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg></button>
                  {Array.from({length:totalPages},(_,i)=>i+1).map(num=><button key={num} className={`frm-pagination-num ${num===safePage?"active":""}`} onClick={()=>setPage(num)}>{num}</button>)}
                  <button className="frm-pagination-btn" disabled={safePage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de visualização */}
      {viewingFormacao && (
        <div className="frm-overlay" onClick={closeView}>
          <div className="frm-modal wide" onClick={e => e.stopPropagation()}>
            <div className="frm-modal-header">
              <div className="frm-modal-title" title={viewingFormacao.titulo || "Formação"}>
                {viewingFormacao.titulo || "Formação"}
              </div>
              <button className="frm-modal-close" onClick={closeView}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="frm-modal-body">
              <div className="frm-view-content" dangerouslySetInnerHTML={{ __html: viewingFormacao.texto }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal de questionário */}
      {viewingPerguntasFormacao && (
        <div className="frm-overlay" onClick={closePerguntas}>
          <div className="frm-modal wide" onClick={e => e.stopPropagation()}>
            <div className="frm-modal-header">
              <div className="frm-modal-title">Questionário — {viewingPerguntasFormacao.titulo}</div>
              <button className="frm-modal-close" onClick={closePerguntas}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="frm-modal-body">
              {perguntasLoading ? (
                <div style={{ textAlign: 'center', padding: 30, color: t.textMuted }}>A carregar perguntas…</div>
              ) : perguntas.length === 0 ? (
                <p style={{ color: t.textMuted }}>Nenhuma pergunta associada a esta formação.</p>
              ) : (
                perguntas.map((p) => (
                  <div key={p.id} className="frm-pergunta-item">
                    <div className="frm-pergunta-titulo">{p.pergunta}</div>
                    <ul className="frm-resposta-lista">
                      {respostasMap[p.id]?.map((r) => (
                        <li key={r.id} className={r.correta === "true" ? "frm-resposta-correta" : "frm-resposta-errada"}>
                          {r.resposta} {r.correta === "true" && "✓"}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de atribuição */}
      {assigningFormacao && (
        <div className="frm-overlay" onClick={closeAssign}>
          <div className="frm-modal" onClick={e => e.stopPropagation()}>
            <div className="frm-modal-header">
              <div className="frm-modal-title" title={assigningFormacao.titulo || "Formação"}>
                {assigningFormacao.titulo || "Atribuir formação"}
              </div>
              <button className="frm-modal-close" onClick={closeAssign}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="frm-modal-body">
              {assignError && <div className="frm-error-banner">{assignError}</div>}

              <div style={{display:"flex", gap: 12, marginBottom: 20}}>
                <button
                  className={`frm-btn-secondary ${assignType==="departamento" ? "active" : ""}`}
                  onClick={() => { setAssignType("departamento"); setSelectedTarget(""); }}
                  style={assignType==="departamento" ? {background: t.bgActive, color: t.textActive, borderColor: t.borderActive} : {}}
                >
                  Departamento
                </button>
                <button
                  className={`frm-btn-secondary ${assignType==="usuario" ? "active" : ""}`}
                  onClick={() => { setAssignType("usuario"); setSelectedTarget(""); }}
                  style={assignType==="usuario" ? {background: t.bgActive, color: t.textActive, borderColor: t.borderActive} : {}}
                >
                  Utilizador
                </button>
              </div>

              <div className="frm-field">
                <label>{assignType === "departamento" ? "Departamento" : "Utilizador"}</label>
                {assignType === "departamento" ? (
                  <select className="frm-select" value={selectedTarget} onChange={e => setSelectedTarget(e.target.value)}>
                    <option value="">Selecionar departamento...</option>
                    {departamentos.map(d => <option key={d.id} value={d.id}>{d.descricao}</option>)}
                  </select>
                ) : (
                  <select className="frm-select" value={selectedTarget} onChange={e => setSelectedTarget(e.target.value)}>
                    <option value="">Selecionar utilizador...</option>
                    {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.email})</option>)}
                  </select>
                )}
                <button className="frm-btn-primary" style={{marginTop:12, width:"fit-content"}} onClick={handleAssign} disabled={assignLoading || !selectedTarget}>
                  {assignLoading ? <span className="mini-spinner"/> : "Atribuir"}
                </button>
              </div>

              <div className="frm-assigned-list">
                <h4 style={{fontSize:"0.9rem", fontWeight:600, marginBottom:12, color:t.textPrimary}}>Utilizadores com esta formação</h4>
                {assignedUsers.length === 0 ? (
                  <p style={{color:t.textMuted, fontSize:"0.84rem"}}>Nenhum utilizador atribuído.</p>
                ) : (
                  assignedUsers.map(u => (
                    <div key={u.id_user} className="frm-assigned-item">
                      <span>{u.nome} {u.departamento ? `(${u.departamento})` : ""}</span>
                      <button className="frm-remove-btn" title="Remover" onClick={() => handleRemove(u.id_user)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className="frm-toast">
          <span className="frm-toast-dot" style={{background: notification.type==="success"? t.success : t.danger}}/>
          {notification.text}
        </div>
      )}
    </div>
  );
}